import React, { useState, FormEvent, ChangeEvent, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import Navbar from 'components/Header/Navbar';
import Button from 'components/Button';
import { listingService } from 'api/services/ListingService';
import { topCategoryService } from 'api/services/TopCategoryService';
import { conditionService } from 'api/services/ConditionService';
import { warrantyService } from 'api/services/WarrantyService';
import { secondaryCategoryService } from 'api/services/SecondaryCategoryService';
import { productTypeService } from 'api/services/ProductTypeService';
import { cityService } from 'api/services/CityService';
import { districtService } from 'api/services/DistrictService';
import type { TopCategoryViewModel } from 'api/types/top-category';
import type { CityViewModel } from 'api/types/city';
import type { DistrictViewModel } from 'api/types/district';
import type { ConditionViewModel } from 'api/types/condition';
import type { WarrantyViewModel } from 'api/types/warranty';
import type { SecondaryCategoryViewModel } from 'api/types/secondary-category';
import type { ProductTypeViewModel } from 'api/types/product-type';
import type { SpecConstraintViewModel } from 'api/types/product-type/SpecConstraintViewModel';
import { SpecValueType } from 'api/types/product-type/SpecConstraintViewModel';
import type { ListingViewModel } from 'api/types/listings/ListingViewModel';
import { MediaService } from 'api/services/MediaService';

interface FilterState {
  mainCategory: string;
  province?: string;
  district?: string;
}

interface FormData {
  category: string;
  secondaryCategoryId: string;
  productTypeId: string;
  brand: string;
  cpuModel: string;
  ram: string;
  storage: string;
  gpu: string;
  price: string;
  title: string;
  description: string;
  condition: string;
  warranty: string;
  testedSystem: boolean;
  city: string;
  district: string;
  neighborhood: string;
  termsAccepted: boolean;
  images: FileList | null;
}

interface SelectedSpecRow {
  specConstraintId: number | null;
  value: string;
}

const LEGACY_SPEC_KEYS = ['Marka', 'İşlemci Modeli', 'RAM', 'Depolama', 'Ekran Kartı'];

function topCategorySlug(tc: TopCategoryViewModel): string {
  const n = (tc.name ?? '').toLowerCase();
  if (n.includes('masaüstü') || n.includes('masaustu')) return 'desktop';
  if (n.includes('dizüstü') || n.includes('laptop')) return 'laptop';
  if (n.includes('çevre') || n.includes('peripheral')) return 'peripherals';
  if (n.includes('monitör') || n.includes('monitor')) return 'monitor';
  if (n.includes('konsol') || n.includes('console')) return 'console';
  if (n.includes('ekipman') || n.includes('equipment')) return 'equipment';
  if (n.includes('sunucu') || n.includes('server')) return 'server';
  return String(tc.id);
}

const initialFormData: FormData = {
  category: '',
  secondaryCategoryId: '',
  productTypeId: '',
  brand: '',
  cpuModel: '',
  ram: '',
  storage: '',
  gpu: '',
  price: '',
  title: '',
  description: '',
  condition: '',
  warranty: '',
  testedSystem: false,
  city: '',
  district: '',
  neighborhood: '',
  termsAccepted: false,
  images: null,
};

function buildSelectedSpecsFromListing(
  specs: Record<string, string> | null | undefined,
  constraints: SpecConstraintViewModel[]
): SelectedSpecRow[] {
  if (!specs || constraints.length === 0) return [];
  const rows: SelectedSpecRow[] = [];
  for (const [key, value] of Object.entries(specs)) {
    if (LEGACY_SPEC_KEYS.includes(key) || !value?.trim()) continue;
    const constraint = constraints.find((c) => c.key === key);
    if (constraint) rows.push({ specConstraintId: constraint.id, value: value.trim() });
  }
  return rows;
}

const Ilan_duzenle: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [topCategories, setTopCategories] = useState<TopCategoryViewModel[]>([]);
  const [conditions, setConditions] = useState<ConditionViewModel[]>([]);
  const [warranties, setWarranties] = useState<WarrantyViewModel[]>([]);
  const [secondaryCategories, setSecondaryCategories] = useState<SecondaryCategoryViewModel[]>([]);
  const [productTypes, setProductTypes] = useState<ProductTypeViewModel[]>([]);
  const [specConstraints, setSpecConstraints] = useState<SpecConstraintViewModel[]>([]);
  const [selectedSpecs, setSelectedSpecs] = useState<SelectedSpecRow[]>([]);
  const [cities, setCities] = useState<CityViewModel[]>([]);
  const [districts, setDistricts] = useState<DistrictViewModel[]>([]);
  const [userForm, setFormData] = useState<FormData>(initialFormData);
  const [filters, setFilters] = useState<FilterState>({
    mainCategory: 'all',
    province: '',
    district: '',
  });
  const [existingImageUrls, setExistingImageUrls] = useState<string[]>([]);
  const [removedImageUrls, setRemovedImageUrls] = useState<string[]>([]);

  const handleChange = (e: ChangeEvent<HTMLSelectElement | HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: checked }));
  };

  const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) setFormData((prev) => ({ ...prev, images: e.target.files }));
  };

  const handleRemoveExistingImage = (url: string) => {
    setRemovedImageUrls((prev) => (prev.includes(url) ? prev : [...prev, url]));
  };

  useEffect(() => {
    const cityId = userForm.city ? Number(userForm.city) : NaN;
    if (!userForm.city || Number.isNaN(cityId)) {
      setDistricts([]);
      return;
    }
    districtService.getAll({ cityId }).then((res) => {
      if (res.isSuccess && res.data) setDistricts(res.data);
    });
  }, [userForm.city]);

  useEffect(() => {
    if (!id) {
      setLoadError('İlan ID bulunamadı.');
      setLoading(false);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const [listingRes, tcRes, condRes, warrRes, citiesRes] = await Promise.all([
          listingService.getById(id),
          topCategoryService.getAll(),
          conditionService.getAll(),
          warrantyService.getAll(),
          cityService.getAll(),
        ]);

        if (cancelled) return;
        if (!listingRes.isSuccess || !listingRes.data) {
          setLoadError(listingRes.message ?? 'İlan yüklenemedi.');
          setLoading(false);
          return;
        }

        if (tcRes.isSuccess && tcRes.data) setTopCategories(tcRes.data);
        if (condRes.isSuccess && condRes.data) setConditions(condRes.data);
        if (warrRes.isSuccess && warrRes.data) setWarranties(warrRes.data);
        if (citiesRes.isSuccess && citiesRes.data) setCities(citiesRes.data);

        const listing: ListingViewModel = listingRes.data;
        const pt = listing.productType;
        const sec = pt?.secondaryCategory;
        let topCategoryId: number | null | undefined = sec?.topCategoryId ?? sec?.topCategory?.id;
        const secondaryCategoryId = sec?.id ?? pt?.secondaryCategoryId;
        const productTypeId = listing.productTypeId ?? pt?.id;

        if (
          (topCategoryId == null || topCategoryId === undefined) &&
          secondaryCategoryId != null &&
          tcRes.isSuccess &&
          tcRes.data?.length
        ) {
          for (const tc of tcRes.data) {
            const secRes = await secondaryCategoryService.getAll({
              topCategoryId: tc.id,
            });
            if (cancelled) break;
            if (
              secRes.isSuccess &&
              secRes.data?.some((sc) => String(sc.id) === String(secondaryCategoryId))
            ) {
              topCategoryId = tc.id;
              break;
            }
          }
        }

        const warrantyList = warrRes.isSuccess && warrRes.data ? warrRes.data : [];
        let warrantyOption = warrantyList.find((w) => {
          if (typeof w.value === 'boolean') return w.value === listing.hasWarranty;
          const v = String(w.value ?? '').toLowerCase();
          return listing.hasWarranty
            ? v === 'true' || v === 'yes' || v === '1'
            : v === 'false' || v === 'no' || v === '0';
        });
        if (!warrantyOption && warrantyList.length > 0) {
          const nameMatch = (name: string | null | undefined, hasWarranty: boolean) => {
            const n = String(name ?? '').toLowerCase();
            if (hasWarranty) return /garanti|var|evet/i.test(n);
            return /yok|garantisiz|hayır/i.test(n);
          };
          warrantyOption = listing.hasWarranty
            ? warrantyList.find((w) => nameMatch(w.name, true)) ?? warrantyList[0]
            : warrantyList.find((w) => nameMatch(w.name, false)) ?? warrantyList[0];
        }
        const warrantyId = warrantyOption ? String(warrantyOption.id) : '';

        const categoryValue =
          topCategoryId != null && topCategoryId !== undefined
            ? String(topCategoryId)
            : '';
        const secondaryValue =
          secondaryCategoryId != null && secondaryCategoryId !== undefined
            ? String(secondaryCategoryId)
            : '';
        const productTypeValue =
          productTypeId != null && productTypeId !== undefined
            ? String(productTypeId)
            : '';

        setFormData({
          ...initialFormData,
          category: categoryValue,
          secondaryCategoryId: secondaryValue,
          productTypeId: productTypeValue,
          brand: listing.specs?.Marka ?? '',
          cpuModel: listing.specs?.['İşlemci Modeli'] ?? '',
          ram: listing.specs?.RAM ?? '',
          storage: listing.specs?.Depolama ?? '',
          gpu: listing.specs?.['Ekran Kartı'] ?? '',
          price: String(listing.price ?? ''),
          title: listing.title ?? '',
          description: listing.description ?? '',
          condition: String(listing.conditionId ?? ''),
          warranty: warrantyId,
          testedSystem: listing.isTested ?? false,
          city: String(listing.cityId ?? ''),
          district: String(listing.districtId ?? ''),
          neighborhood: '',
          termsAccepted: true,
          images: null,
        });

        const tc =
          tcRes.isSuccess && tcRes.data && categoryValue
            ? tcRes.data.find((c) => String(c.id) === String(categoryValue))
            : undefined;
        if (tc) setFilters((prev) => ({ ...prev, mainCategory: topCategorySlug(tc) }));

        if (categoryValue && !Number.isNaN(Number(categoryValue))) {
          const secRes = await secondaryCategoryService.getAll({
            topCategoryId: Number(categoryValue),
          });
          if (!cancelled && secRes.isSuccess && secRes.data) setSecondaryCategories(secRes.data);
        }

        if (secondaryCategoryId != null && !Number.isNaN(Number(secondaryCategoryId))) {
          const ptRes = await productTypeService.getAll({
            secondaryCategoryId: Number(secondaryCategoryId),
          });
          if (!cancelled && ptRes.isSuccess && ptRes.data) setProductTypes(ptRes.data);
        }

        if (productTypeId != null && !Number.isNaN(Number(productTypeId))) {
          const specRes = await productTypeService.getSpecConstraints(Number(productTypeId));
          if (!cancelled && specRes.isSuccess && specRes.data) {
            setSpecConstraints(specRes.data);
            setSelectedSpecs(
              buildSelectedSpecsFromListing(listing.specs ?? null, specRes.data)
            );
          }
        }

        if (listing.cityId != null && !Number.isNaN(listing.cityId)) {
          const distRes = await districtService.getAll({ cityId: listing.cityId });
          if (!cancelled && distRes.isSuccess && distRes.data) setDistricts(distRes.data);
        }

        const urls =
          listing.imageUrls?.length
            ? listing.imageUrls
            : (listing.images ?? [])
                .map((img) => (img.url != null && img.url !== '' ? img.url : String(img.id)))
                .filter(Boolean);
        if (!cancelled) {
          setExistingImageUrls(urls);
          setRemovedImageUrls([]);
        }
      } catch (err) {
        if (!cancelled)
          setLoadError(err instanceof Error ? err.message : 'İlan yüklenirken bir hata oluştu.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const handleTopCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    const tc = topCategories.find((c) => String(c.id) === value);
    setFormData((prev) => ({
      ...prev,
      category: value,
      secondaryCategoryId: '',
      productTypeId: '',
    }));
    setSecondaryCategories([]);
    setProductTypes([]);
    setSpecConstraints([]);
    setSelectedSpecs([]);
    if (tc) {
      setFilters((prev) => ({ ...prev, mainCategory: topCategorySlug(tc) }));
      const topCategoryIdNum = Number(value);
      if (!Number.isNaN(topCategoryIdNum)) {
        secondaryCategoryService.getAll({ topCategoryId: topCategoryIdNum }).then((res) => {
          if (res.isSuccess && res.data) setSecondaryCategories(res.data);
        });
      }
    } else {
      setFilters((prev) => ({ ...prev, mainCategory: value || 'all' }));
    }
  };

  const handleCityChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFormData((prev) => ({ ...prev, city: e.target.value, district: '' }));
  };

  const handleDistrictChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFormData((prev) => ({ ...prev, district: e.target.value }));
  };

  const handleSecondaryCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, secondaryCategoryId: value, productTypeId: '' }));
    setProductTypes([]);
    setSpecConstraints([]);
    setSelectedSpecs([]);
    const secondaryIdNum = Number(value);
    if (value && !Number.isNaN(secondaryIdNum)) {
      productTypeService.getAll({ secondaryCategoryId: secondaryIdNum }).then((res) => {
        if (res.isSuccess && res.data) setProductTypes(res.data);
      });
    }
  };

  const handleProductTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, productTypeId: value }));
    setSpecConstraints([]);
    setSelectedSpecs([]);
    if (value && !Number.isNaN(Number(value))) {
      productTypeService.getSpecConstraints(Number(value)).then((res) => {
        if (res.isSuccess && res.data) setSpecConstraints(res.data);
      });
    }
  };

  const usedSpecConstraintIds = selectedSpecs
    .map((r) => r.specConstraintId)
    .filter((id): id is number => id != null);
  const availableSpecConstraints = specConstraints.filter(
    (sc) => !usedSpecConstraintIds.includes(sc.id)
  );
  const canAddSpec =
    specConstraints.length > 0 && selectedSpecs.length < specConstraints.length;

  const addSpecRow = () => {
    setSelectedSpecs((prev) => [...prev, { specConstraintId: null, value: '' }]);
  };

  const updateSpecConstraint = (rowIndex: number, specConstraintId: number) => {
    setSelectedSpecs((prev) =>
      prev.map((row, i) =>
        i === rowIndex ? { ...row, specConstraintId, value: '' } : row
      )
    );
  };

  const updateSpecValue = (rowIndex: number, value: string) => {
    setSelectedSpecs((prev) =>
      prev.map((row, i) => (i === rowIndex ? { ...row, value } : row))
    );
  };

  const removeSpecRow = (rowIndex: number) => {
    setSelectedSpecs((prev) => prev.filter((_, i) => i !== rowIndex));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!id) return;
    if (!userForm.title || !userForm.price) {
      alert('Lütfen gerekli alanları doldurun');
      return;
    }
    if (!userForm.termsAccepted) {
      alert('Lütfen kullanım koşullarını kabul edin');
      return;
    }
    const productTypeId = userForm.productTypeId;
    if (productTypes.length > 0 && !productTypeId) {
      alert('Lütfen ürün tipini seçin.');
      return;
    }

    const city = filters.province || userForm.city;
    const district = filters.district || userForm.district;
    const specs: Record<string, string> = {
      ...(userForm.brand && { Marka: userForm.brand }),
      ...(userForm.cpuModel && { 'İşlemci Modeli': userForm.cpuModel }),
      ...(userForm.ram && { RAM: userForm.ram }),
      ...(userForm.storage && { Depolama: userForm.storage }),
      ...(userForm.gpu && { 'Ekran Kartı': userForm.gpu }),
    };
    const specsJson: Record<string, string> = {};
    selectedSpecs.forEach((row) => {
      if (row.specConstraintId == null || !row.value.trim()) return;
      const constraint = specConstraints.find((sc) => sc.id === row.specConstraintId);
      if (constraint) specsJson[constraint.key] = row.value.trim();
    });

    setSubmitting(true);
    try {
      const form = new FormData();
      form.append('Title', userForm.title);
      form.append('Description', userForm.description ?? '');
      form.append('Price', String(userForm.price));
      form.append('ConditionId', userForm.condition || '');
      form.append('ProductTypeId', productTypeId || '');
      form.append('CityId', city || '');
      form.append('DistrictId', district || '');
      const hasWarrantyFromApi =
        warranties.find((w) => String(w.id) === userForm.warranty)?.value === true;
      form.append(
        'HasWarranty',
        (hasWarrantyFromApi ?? userForm.warranty === 'yes') ? 'true' : 'false'
      );
      if (Object.keys(specs).length > 0) form.append('Specs', JSON.stringify(specs));
      if (Object.keys(specsJson).length > 0)
        form.append('SpecsJson', JSON.stringify(specsJson));
      if (userForm.images?.length) {
        for (let i = 0; i < userForm.images.length; i++) {
          const file = userForm.images[i];
          if (file?.type.startsWith('image/')) form.append('Images', file);
        }
      }
      if (removedImageUrls.length > 0) {
        form.append('RemovedImageUrls', JSON.stringify(removedImageUrls));
      }

      const result = await listingService.update(id, form);
      if (result.isSuccess) {
        alert('İlan güncellendi.');
        navigate('/ilanlarim');
      } else {
        alert(result.message ?? 'İlan güncellenemedi.');
      }
    } catch (err) {
      console.error('İlan güncelleme hatası:', err);
      alert(
        err instanceof Error ? err.message : 'İlan güncellenirken bir hata oluştu'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = () => setShowDeleteConfirm(true);
  const handleCancelDelete = () => setShowDeleteConfirm(false);
  const handleConfirmDelete = async () => {
    if (!id) return;
    setDeleting(true);
    try {
      const result = await listingService.delete(id);
      if (result.isSuccess) {
        setShowDeleteConfirm(false);
        navigate('/ilanlarim');
      } else {
        alert(result.message ?? 'İlan silinemedi.');
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : 'İlan silinirken bir hata oluştu.');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="container mt-4 text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Yükleniyor...</span>
          </div>
          <p className="mt-2 text-muted">İlan yükleniyor...</p>
        </div>
      </>
    );
  }

  if (loadError) {
    return (
      <>
        <Navbar />
        <div className="container mt-4">
          <div className="alert alert-danger">{loadError}</div>
          <Button className="btn btn-outline-secondary" onClick={() => navigate('/ilanlarim')}>
            İlanlarıma Dön
          </Button>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="container mt-4">
        <div className="row justify-content-center">
          <div className="col-lg-8">
            <div className="card shadow-sm">
              <div className="card-body p-4">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-4">
                  <h2 className="card-title text-center mb-0">İlan Düzenle</h2>
                  <div className="d-flex gap-2">
                    <Button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => navigate('/ilanlarim')}
                    >
                      <i className="fas fa-arrow-left me-2"></i>
                      İlanlarıma Dön
                    </Button>
                    <Button
                      type="button"
                      className="btn btn-danger"
                      onClick={handleDeleteClick}
                      disabled={deleting}
                      loading={deleting}
                    >
                      <i className="fas fa-trash me-2"></i>
                      İlanı Sil
                    </Button>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="needs-validation">
                  <div className="card mb-4">
                    <div className="card-body">
                      <h5 className="card-title mb-3">İlan Özellikleri</h5>

                      <div className="mb-3">
                        <label className="form-label">Tüm Kategoriler</label>
                        <select
                          className="form-select"
                          name="category"
                          value={userForm.category}
                          onChange={handleTopCategoryChange}
                          required
                        >
                          <option value="">Seçiniz</option>
                          {topCategories.map((tc) => (
                            <option key={tc.id} value={String(tc.id)}>
                              {tc.name ?? `Kategori ${tc.id}`}
                            </option>
                          ))}
                        </select>
                      </div>

                      {secondaryCategories.length > 0 && (
                        <div className="mb-3">
                          <label className="form-label">Alt Kategori</label>
                          <select
                            className="form-select"
                            name="secondaryCategoryId"
                            value={userForm.secondaryCategoryId}
                            onChange={handleSecondaryCategoryChange}
                          >
                            <option value="">Seçiniz</option>
                            {secondaryCategories.map((sc) => (
                              <option key={sc.id} value={String(sc.id)}>
                                {sc.name ?? `Kategori ${sc.id}`}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      {productTypes.length > 0 && (
                        <div className="mb-3">
                          <label className="form-label">Ürün Tipi</label>
                          <select
                            className="form-select"
                            name="productTypeId"
                            value={userForm.productTypeId}
                            onChange={handleProductTypeChange}
                            required
                          >
                            <option value="">Seçiniz</option>
                            {productTypes.map((pt) => (
                              <option key={pt.id} value={String(pt.id)}>
                                {pt.name ?? `Ürün tipi ${pt.id}`}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      {specConstraints.length > 0 && (
                        <div className="mb-3">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <label className="form-label mb-0">Ürün Özellikleri</label>
                            {canAddSpec && selectedSpecs.length === 0 && (
                              <Button
                                type="button"
                                className="btn btn-outline-primary btn-sm"
                                onClick={addSpecRow}
                              >
                                Özellik Ekle
                              </Button>
                            )}
                          </div>
                          {selectedSpecs.map((row, rowIndex) => {
                            const constraint =
                              row.specConstraintId != null
                                ? specConstraints.find((sc) => sc.id === row.specConstraintId)
                                : null;
                            const valueType = constraint?.valueType ?? SpecValueType.String;
                            const isEnum = valueType === SpecValueType.Enum;
                            const allowedValues = constraint?.allowedValues ?? [];
                            const controlHeightStyle = { minHeight: '38px' };

                            return (
                              <div
                                key={rowIndex}
                                className="d-flex align-items-stretch gap-2 mb-2"
                              >
                                {row.specConstraintId == null ? (
                                  <select
                                    className="form-select flex-grow-1"
                                    style={controlHeightStyle}
                                    value=""
                                    onChange={(e) => {
                                      const sid = e.target.value ? Number(e.target.value) : 0;
                                      if (sid) updateSpecConstraint(rowIndex, sid);
                                    }}
                                    aria-label="Özellik seçin"
                                  >
                                    <option value="">Özellik seçin</option>
                                    {availableSpecConstraints.map((sc) => (
                                      <option key={sc.id} value={sc.id}>
                                        {sc.displayName}
                                      </option>
                                    ))}
                                  </select>
                                ) : (
                                  <>
                                    <select
                                      className="form-select flex-grow-1"
                                      style={{
                                        ...controlHeightStyle,
                                        flex: '1 1 50%',
                                        maxWidth: '50%',
                                      }}
                                      value={row.specConstraintId}
                                      disabled
                                      aria-label={constraint?.displayName}
                                    >
                                      <option value={row.specConstraintId}>
                                        {constraint?.displayName}
                                      </option>
                                    </select>
                                    {isEnum ? (
                                      <select
                                        className="form-select flex-grow-1"
                                        style={{
                                          ...controlHeightStyle,
                                          flex: '1 1 50%',
                                          maxWidth: '50%',
                                        }}
                                        value={row.value}
                                        onChange={(e) =>
                                          updateSpecValue(rowIndex, e.target.value)
                                        }
                                        aria-label={`${constraint?.displayName} değeri`}
                                      >
                                        <option value="">Seçiniz</option>
                                        {allowedValues.map((av) => (
                                          <option key={av.id} value={av.value}>
                                            {av.value}
                                          </option>
                                        ))}
                                      </select>
                                    ) : (
                                      <input
                                        type={
                                          valueType === SpecValueType.Number ? 'number' : 'text'
                                        }
                                        className="form-control flex-grow-1"
                                        style={{
                                          ...controlHeightStyle,
                                          flex: '1 1 50%',
                                          maxWidth: '50%',
                                        }}
                                        value={row.value}
                                        onChange={(e) =>
                                          updateSpecValue(rowIndex, e.target.value)
                                        }
                                        placeholder={constraint?.displayName}
                                        aria-label={`${constraint?.displayName} değeri`}
                                      />
                                    )}
                                  </>
                                )}
                                <Button
                                  type="button"
                                  className="btn btn-outline-danger btn-sm align-self-center flex-shrink-0"
                                  onClick={() => removeSpecRow(rowIndex)}
                                  aria-label="Özelliği kaldır"
                                >
                                  ×
                                </Button>
                              </div>
                            );
                          })}
                          {canAddSpec && selectedSpecs.length > 0 && (
                            <div className="mt-2">
                              <Button
                                type="button"
                                className="btn btn-outline-primary btn-sm"
                                onClick={addSpecRow}
                              >
                                Özellik Ekle
                              </Button>
                            </div>
                          )}
                        </div>
                      )}

                      <div className="mb-3 mt-4">
                        <h6 className="mb-3">Ürün Durumu</h6>
                        <select
                          className="form-select"
                          name="condition"
                          value={userForm.condition}
                          onChange={handleChange}
                          required
                        >
                          <option value="">Seçiniz</option>
                          {conditions.map((c) => (
                            <option key={c.id} value={String(c.id)}>
                              {c.name ?? `Durum ${c.id}`}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="mb-3 mt-4">
                        <h6 className="mb-3">Garanti Durumu</h6>
                        <select
                          className="form-select"
                          name="warranty"
                          value={userForm.warranty}
                          onChange={handleChange}
                          required
                        >
                          <option value="">Seçiniz</option>
                          {warranties.map((w) => (
                            <option key={w.id} value={String(w.id)}>
                              {w.name ?? `Garanti ${w.id}`}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="form-group mb-3">
                    <label className="form-label">İlan Başlığı</label>
                    <input
                      type="text"
                      className="form-control"
                      name="title"
                      value={userForm.title}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="form-group mb-3">
                    <label className="form-label">Açıklama</label>
                    <textarea
                      className="form-control"
                      name="description"
                      value={userForm.description}
                      onChange={handleChange}
                      rows={4}
                      required
                    />
                  </div>

                  <div className="card mb-4 border-0 shadow-sm">
                    <div className="card-body p-4">
                      <h5 className="card-title mb-3 fw-bold">Adres Bilgileri</h5>
                      <div className="row g-3">
                        <div className="col-md-6">
                          <label className="form-label">İl</label>
                          <select
                            className="form-select"
                            name="city"
                            value={userForm.city}
                            onChange={handleCityChange}
                            required
                            aria-label="İl seçin"
                          >
                            <option value="">Seçiniz</option>
                            {cities.map((c) => (
                              <option key={c.id} value={String(c.id)}>
                                {c.name ?? `İl ${c.id}`}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">İlçe</label>
                          <select
                            className="form-select"
                            name="district"
                            value={userForm.district}
                            onChange={handleDistrictChange}
                            required
                            disabled={!userForm.city}
                            aria-label="İlçe seçin"
                          >
                            <option value="">Seçiniz</option>
                            {districts.map((d) => (
                              <option key={d.id} value={String(d.id)}>
                                {d.name ?? `İlçe ${d.id}`}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="card mb-4 border-0 shadow-sm">
                    <div className="card-body p-4">
                      {existingImageUrls.filter((url) => !removedImageUrls.includes(url)).length >
                        0 && (
                        <div className="form-group mb-4">
                          <h5 className="card-title mb-3 fw-bold">Mevcut Fotoğraflar</h5>
                          <div className="d-flex flex-wrap gap-3">
                            {existingImageUrls
                              .filter((url) => !removedImageUrls.includes(url))
                              .map((url, index) => (
                                <div
                                  key={`${url}-${index}`}
                                  className="position-relative rounded overflow-hidden border bg-light"
                                  style={{ width: 120, height: 120 }}
                                >
                                  <img
                                    src={MediaService.toImageSrc(url)}
                                    alt={`İlan fotoğrafı ${index + 1}`}
                                    className="w-100 h-100 object-fit-cover"
                                    style={{ objectFit: 'cover' }}
                                    loading="lazy"
                                  />
                                  <Button
                                    type="button"
                                    className="btn btn-danger btn-sm position-absolute top-0 end-0 m-1 rounded-circle p-0 d-flex align-items-center justify-content-center"
                                    style={{ width: 28, height: 28 }}
                                    onClick={() => handleRemoveExistingImage(url)}
                                    aria-label="Fotoğrafı kaldır"
                                  >
                                    <i className="fas fa-times" aria-hidden />
                                  </Button>
                                </div>
                              ))}
                          </div>
                          <small className="text-muted d-block mt-2">
                            Bu fotoğraflar ilanınızda yayınlanmaktadır. Kaldırmak için ×
                            butonuna tıklayın. Yeni fotoğraf eklemek için aşağıyı kullanın.
                          </small>
                        </div>
                      )}

                      <div className="form-group mb-4">
                        <h5 className="card-title mb-3 fw-bold">Fotoğraf ve Video Ekle</h5>
                        <input
                          type="file"
                          className="form-control"
                          accept="image/*,video/*"
                          multiple
                          onChange={handleImageChange}
                        />
                        <small className="text-muted">
                          Yeni fotoğraf ekleyebilirsiniz (isteğe bağlı). Mevcut fotoğraflar
                          korunur.
                        </small>
                      </div>

                      <hr className="my-4" />

                      <h5 className="card-title mb-3 fw-bold">
                        Test Edilen Sistem Rozeti Almak İstiyor musunuz?
                      </h5>
                      <p className="card-text mb-3">
                        Test edilen sistem rozeti, bilgisayarınızın temel bileşenlerinin
                        (işlemci, RAM, disk, ekran kartı) test edildiğini ve kararlı çalıştığını
                        gösteren bir işarettir.
                      </p>
                      <div className="form-check mb-3">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="testedSystem"
                          name="testedSystem"
                          checked={userForm.testedSystem}
                          onChange={handleCheckboxChange}
                        />
                        <label className="form-check-label" htmlFor="testedSystem">
                          Evet, sistemimi test etmek ve rozet almak istiyorum
                        </label>
                      </div>
                      <div className="accordion mt-3" id="testDetailsAccordionEdit">
                        <div className="accordion-item border-0 sb-surface-muted rounded mb-2">
                          <h2 className="accordion-header" id="headingOneEdit">
                            <Button
                              className="accordion-button collapsed sb-surface-muted"
                              type="button"
                              data-bs-toggle="collapse"
                              data-bs-target="#collapseOneEdit"
                              aria-expanded="false"
                              aria-controls="collapseOneEdit"
                            >
                              <strong>1. İşlemci Testi</strong>
                            </Button>
                          </h2>
                          <div
                            id="collapseOneEdit"
                            className="accordion-collapse collapse"
                            aria-labelledby="headingOneEdit"
                            data-bs-parent="#testDetailsAccordionEdit"
                          >
                            <div className="accordion-body">
                              <p>
                                İşlemcinin kararlılığını ve sıcaklığının görmek için CoreTemp ve
                                Cpu-z programı kullanılır.
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="accordion-item border-0 sb-surface-muted rounded mb-2">
                          <h2 className="accordion-header" id="headingTwoEdit">
                            <Button
                              className="accordion-button collapsed sb-surface-muted"
                              type="button"
                              data-bs-toggle="collapse"
                              data-bs-target="#collapseTwoEdit"
                              aria-expanded="false"
                              aria-controls="collapseTwoEdit"
                            >
                              <strong>2. RAM Testi</strong>
                            </Button>
                          </h2>
                          <div
                            id="collapseTwoEdit"
                            className="accordion-collapse collapse"
                            aria-labelledby="headingTwoEdit"
                            data-bs-parent="#testDetailsAccordionEdit"
                          >
                            <div className="accordion-body">
                              <p>Techpowerup Memtest64 programı ile Windows üzerinde test yapılır.</p>
                            </div>
                          </div>
                        </div>
                        <div className="accordion-item border-0 sb-surface-muted rounded mb-2">
                          <h2 className="accordion-header" id="headingThreeEdit">
                            <Button
                              className="accordion-button collapsed sb-surface-muted"
                              type="button"
                              data-bs-toggle="collapse"
                              data-bs-target="#collapseThreeEdit"
                              aria-expanded="false"
                              aria-controls="collapseThreeEdit"
                            >
                              <strong>3. HDD/SSD Sağlamlık Testi</strong>
                            </Button>
                          </h2>
                          <div
                            id="collapseThreeEdit"
                            className="accordion-collapse collapse"
                            aria-labelledby="headingThreeEdit"
                            data-bs-parent="#testDetailsAccordionEdit"
                          >
                            <div className="accordion-body">
                              <p>HDD Sentinel programı ile sağlık ve performans değerleri kontrol edilir.</p>
                            </div>
                          </div>
                        </div>
                        <div className="accordion-item border-0 sb-surface-muted rounded">
                          <h2 className="accordion-header" id="headingFourEdit">
                            <Button
                              className="accordion-button collapsed sb-surface-muted"
                              type="button"
                              data-bs-toggle="collapse"
                              data-bs-target="#collapseFourEdit"
                              aria-expanded="false"
                              aria-controls="collapseFourEdit"
                            >
                              <strong>4. Ekran Kartı Testi</strong>
                            </Button>
                          </h2>
                          <div
                            id="collapseFourEdit"
                            className="accordion-collapse collapse"
                            aria-labelledby="headingFourEdit"
                            data-bs-parent="#testDetailsAccordionEdit"
                          >
                            <div className="accordion-body">
                              <p>Geeks3D Furmark programı ile ekran kartı kararlılık testi yapılır.</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="form-group mb-3">
                    <label className="form-label">Fiyat (TL)</label>
                    <input
                      type="number"
                      className="form-control"
                      name="price"
                      value={userForm.price}
                      onChange={handleChange}
                      required
                      min="0"
                    />
                  </div>

                  <div className="form-check mb-4 mt-4">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="termsEdit"
                      name="termsAccepted"
                      checked={userForm.termsAccepted}
                      onChange={handleCheckboxChange}
                      required
                    />
                    <label className="form-check-label" htmlFor="termsEdit">
                      <a
                        href="#"
                        className="text-decoration-none"
                        data-bs-toggle="modal"
                        data-bs-target="#termsModalEdit"
                      >
                        İlan Sözleşmesi
                      </a>
                      'ni okudum ve kabul ediyorum
                    </label>
                  </div>

                  <div className="text-center">
                    <Button
                      type="submit"
                      className="btn btn-primary btn-lg w-100"
                      disabled={!userForm.termsAccepted || submitting}
                      loading={submitting}
                    >
                      Değişiklikleri Kaydet
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>

        <div
          className="modal fade"
          id="termsModalEdit"
          tabIndex={-1}
          aria-labelledby="termsModalLabelEdit"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="termsModalLabelEdit">
                  İlan Sözleşmesi
                </h5>
                <Button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Kapat"
                ></Button>
              </div>
              <div className="modal-body">
                <h6>SadeceBilgisayar.com İlan Kullanım Koşulları</h6>
                <p>
                  Bu sözleşme, SadeceBilgisayar.com platformunda ilan veren kullanıcılar ile site
                  yönetimi arasındaki hak ve yükümlülükleri belirler.
                </p>
                <h6>1. Genel Kurallar</h6>
                <p>
                  - Kullanıcılar, platformda verdikleri ilanların içeriğinden tamamen kendileri
                  sorumludur.<br />
                  - Telif hakkı ihlali içeren içerikler paylaşılamaz.<br />
                  - Yasadışı ürünlerin satışına yönelik ilanlar yayınlanamaz.
                </p>
                <p className="mb-0">
                  <Link to="/ilan-sozlesmesi" target="_blank" rel="noopener noreferrer" className="text-primary">
                    Tam metni görüntüle
                  </Link>
                </p>
              </div>
              <div className="modal-footer">
                <Button type="button" className="btn btn-secondary" data-bs-dismiss="modal">
                  Kapat
                </Button>
              </div>
            </div>
          </div>
        </div>

        {showDeleteConfirm && (
          <div
            className="modal show"
            style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
            tabIndex={-1}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header bg-danger text-white">
                  <h5 className="modal-title">Onay</h5>
                  <Button
                    type="button"
                    className="btn-close btn-close-white"
                    onClick={handleCancelDelete}
                    aria-label="Kapat"
                  ></Button>
                </div>
                <div className="modal-body">
                  <p>İlanı silmek istediğinizden emin misiniz?</p>
                  <p className="text-muted small">Bu işlem geri alınamaz.</p>
                </div>
                <div className="modal-footer">
                  <Button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleCancelDelete}
                  >
                    İptal
                  </Button>
                  <Button
                    type="button"
                    className="btn btn-danger"
                    onClick={handleConfirmDelete}
                    disabled={deleting}
                    loading={deleting}
                  >
                    Evet, Sil
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Ilan_duzenle;
